// THE CALL'S THEATER — Fly in a call, on the main area (docs/FLY_MODE.md
// §3.3; Nika 2026-10-18: "minimize call and show it in main mode … main mode
// is theater in different modes"). The call's pages (meet functions/
// meetFly.js — anyone's ask, by voice or typed, made by the asker's own
// client and shared with everyone) take the main layer OVER the call: the
// call stays mounted underneath (its audio keeps playing) and minimizes into
// the dock (meet components/CallWidget.js); "Back to call" — or the dock's
// own button — gives it back.
//
// The SAME page as a person's own Fly canvas (AssistantFlyPageBody: eyebrow,
// title, who asked, the opening, the live sections, the next asks — a next ask
// stays in the call), the same folded rows for the call's other pages. The
// call's conversation is its chat; the theater keeps no rail of its own.
//
// Every value comes from the meet package through `el.call` (registered once
// the call's meet context is folded — the theater only ever shows on the
// call's own view, so they are there). Mounted in the main layer
// (components/AssistantMainLayer.js) like the other main-area views.
export const AssistantFlyCallView = {
  tag: 'section',
  role: 'region',
  dataFlyCall: true,
  ariaLabel: (el) => el.call('polyglot', 'workspace.assistantFly.callLabel'),
  // The grid metrics — geometry of THIS element, so its own state.
  state: { cols: 0, gutter: 0, cell: 0, square: true },

  dataOpen: (el, s) =>
    el.call('assistantFlyCallDo', 'meetFlyTheaterShown', s.root) ? 'true' : 'false',
  ariaHidden: (el, s) =>
    el.call('assistantFlyCallDo', 'meetFlyTheaterShown', s.root) ? null : 'true',

  display: 'flex',
  flexDirection: 'column',
  gap: 'B',
  width: '100%',
  maxWidth: 'var(--home-grid-w, 100%)',
  marginInline: 'auto',
  minWidth: '0',
  boxSizing: 'border-box',
  paddingBottom: 'C',
  opacity: '0',
  visibility: 'hidden',
  pointerEvents: 'none',
  transition: 'opacity C defaultBezier, visibility 0s linear C',
  '&[data-open="true"]': {
    opacity: '1',
    visibility: 'visible',
    pointerEvents: 'auto',
    transition: 'opacity C defaultBezier, visibility 0s linear 0s'
  },

  // The meet functions are reached through one guarded door
  // (functions/assistantFlyCall.js assistantFlyCallDo): this view can render
  // before the call's meet context is folded. Every part that shows the call
  // DECLARES `meetFly` as its dependency (childrenDeps / textDeps / …): its
  // first run may read nothing through an unregistered name, and a factory
  // that read no signal never re-runs.
  subscribeTo: ['meetFly', 'currentPath', 'meetView', 'livekitConnected'],
  onMounted: (el, s) => {
    el.call('assistantBoardSyncGrid', el, s)
    // Escape gives the call back (functions/assistantFlyCall.js).
    el.call('assistantFlyCallShortcuts', el)
  },
  onUpdate: (el, s) => el.call('assistantBoardGridRead', el, s),
  onWindowResize: (e, el, s) => el.call('assistantBoardSyncGrid', el, s),
  onRemove: (el) => {
    if (typeof el.__flyCallShortcutsOff === 'function') el.__flyCallShortcutsOff()
    el.call('assistantBoardGridReadOff', el)
  },

  // ── The bar IS the call while the theater covers it (Nika 2026-10-23: "when
  // fly is running in call, keep some call ui/ux so we understand its the
  // call"): the live dot, the room, who is in it and the mic, the live
  // caption, then Stop or Hide · mic · Back to call · Leave. A glass island,
  // like the dock it stands in for here (meet components/CallWidget.js hides
  // while the theater shows). The call's values come from the meet package
  // (functions/meetCallStrip.js) through the one door, each part declaring
  // the root signal it reads.
  CallBar: {
    flow: 'x',
    flexWrap: 'wrap',
    align: 'center space-between',
    gap: 'Z1',
    minWidth: '0',
    minHeight: 'controlHCompact',
    width: '100%',
    marginInline: 'auto',
    boxSizing: 'border-box',
    padding: 'Z Z1',
    round: 'radiusSheet',
    theme: 'canvas-card-glass',
    style: (el, s) => el.call('assistantFlyPageStyle', s),

    CallMark: {
      flow: 'x',
      align: 'center',
      gap: 'Y1',
      minWidth: '0',
      color: 'title',
      // Live; it rings while someone talks.
      LiveDot: {
        tag: 'span',
        boxSize: 'X2',
        minWidth: 'X2',
        round: '100%',
        // the success ink pair (one green per scheme), never a raw hue
        background: 'successInk',
        transition: 'box-shadow B defaultBezier',
        isSpeakingDeps: [(el, s) => s.root && s.root.livekitSpeakers],
        isSpeaking: (el, s) => !!el.call('assistantFlyCallDo', 'meetCallSpeaking', s.root),
        '.isSpeaking': {
          boxShadow: '0 0 0 X1 color-mix(in srgb, var(--color-green) 30%, transparent)'
        }
      },
      Icon: { name: 'pxFly', boxSize: 'iconSm', flexShrink: '0' },
      CallName: {
        tag: 'span',
        fontSize: 'Z2',
        fontWeight: '500',
        minWidth: '0',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        text: (el, s) => {
          const r = s.root || {}
          const room = (r.rooms || []).find((x) => x && x.id === r.activeRoomId)
          return room && room.name
            ? String(el.call('polyglot', 'workspace.assistantFly.callTitle')).replace(
                '{room}',
                room.name
              )
            : el.call('polyglot', 'workspace.assistantFly.callTitleBare')
        }
      },
      // "2 in call · mic on" (meet.widget.participantLine, the dock's line).
      CallPeople: {
        tag: 'span',
        textDeps: [(el, s) => s.root && s.root.livekitParticipants],
        fontSize: 'Z2',
        color: 'caption',
        whiteSpace: 'nowrap',
        text: (el, s) => el.call('assistantFlyCallDo', 'meetCallStripLine', s.root) || ''
      },
      CallCount: {
        tag: 'span',
        textDeps: ['meetFly'],
        fontSize: 'Z2',
        color: 'inactive',
        whiteSpace: 'nowrap',
        text: (el, s) => {
          const n = (el.call('assistantFlyCallDo', 'meetFlyPages', s.root) || []).length
          return n > 1
            ? String(el.call('polyglot', 'workspace.assistantFly.pageCount')).replace(
                '{n}',
                String(n)
              )
            : ''
        }
      }
    },

    // The call's live line: who talks and what they said, while it is still
    // "now". It takes the free middle of the bar, so a line arriving or
    // ending moves nothing.
    CallCaption: {
      tag: 'span',
      textDeps: [
        (el, s) => s.root && s.root.utterances,
        (el, s) => s.root && s.root._meetingInviteTick
      ],
      flex: '1',
      minWidth: '0',
      fontSize: 'Z2',
      color: 'caption',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      text: (el, s) => el.call('assistantFlyCallDo', 'meetCallStripCaption', s.root) || ''
    },

    CallActions: {
      flow: 'x',
      flexWrap: 'wrap',
      align: 'center',
      gap: 'Y',
      minWidth: '0',
      // The open page off the theater: its asker stops showing it for
      // everyone, anyone else hides it for themselves.
      AssistantIconButton: {
        dataFlyCallStop: true,
        ariaLabelDeps: ['meetFly'],
        titleDeps: ['meetFly'],
        ariaLabel: (el, s) => {
          const p = el.call('assistantFlyCallDo', 'meetFlyPage', s.root)
          return el.call(
            'polyglot',
            p && p.mine ? 'workspace.assistantFly.callStop' : 'workspace.assistantFly.callHide'
          )
        },
        title: (el, s) => {
          const p = el.call('assistantFlyCallDo', 'meetFlyPage', s.root)
          return el.call(
            'polyglot',
            p && p.mine ? 'workspace.assistantFly.callStop' : 'workspace.assistantFly.callHide'
          )
        },
        onClick: (e, el, s) => {
          const p = el.call('assistantFlyCallDo', 'meetFlyPage', s.root)
          if (p) el.call('assistantFlyCallDo', 'meetFlyDismiss', p.key)
        },
        Icon: { name: 'x' }
      },
      // The mic, without leaving the pages.
      AssistantIconButton_mic: {
        dataFlyCallMic: true,
        isOffDeps: [(el, s) => s.root && s.root.livekitParticipants],
        isOff: (el, s) => !el.call('assistantFlyCallDo', 'meetCallMicOn', s.root),
        ariaPressedDeps: [(el, s) => s.root && s.root.livekitParticipants],
        ariaPressed: (el, s) =>
          el.call('assistantFlyCallDo', 'meetCallMicOn', s.root) ? 'true' : 'false',
        ariaLabel: (el) => el.call('polyglot', 'meet.widget.toggleMic'),
        title: (el) => el.call('polyglot', 'meet.widget.toggleMic'),
        '.isOff': { color: 'dangerInk' },
        onClick: (e, el) => el.call('assistantFlyCallDo', 'meetCallMicToggle'),
        Icon: {
          nameDeps: [(el, s) => s.root && s.root.livekitParticipants],
          name: (el, s) =>
            el.call('assistantFlyCallDo', 'meetCallMicOn', s.root) ? 'mic' : 'micOff'
        }
      },
      AppCapsuleBtnToolbar: {
        dataFlyCallBack: true,
        compact: true,
        text: (el) => el.call('polyglot', 'meet.fly.backToCall'),
        onClick: (e, el) => el.call('assistantFlyCallDo', 'meetFlyTheater', false)
      },
      // Leave from the pages: the call ends for me, and its theater with it.
      AppCapsuleBtnDanger: {
        dataFlyCallLeave: true,
        compact: true,
        text: (el) => el.call('polyglot', 'meet.widget.leaveCall'),
        onClick: async (e, el, s) => {
          await el.call('assistantFlyCallDo', 'disconnectCall')
          s.root?.update({ meetView: 'list' })
        }
      }
    }
  },

  // ── The call's earlier pages, folded ──────────────────────────────────────
  CallBefore: {
    childrenDeps: ['meetFly'],
    tag: 'ol',
    margin: '0',
    padding: '0',
    listStyle: 'none',
    flow: 'y',
    gap: 'X',
    minWidth: '0',
    width: '100%',
    marginInline: 'auto',
    boxSizing: 'border-box',
    style: (el, s) => el.call('assistantFlyPageStyle', s),
    children: (el, s) => {
      const pages = el.call('assistantFlyCallDo', 'meetFlyPages', s.root) || []
      const at = pages.findIndex((p) => p.open)
      return pages.filter((p, i) => i < at).slice(-5)
    },
    childrenAs: 'state',
    childExtends: 'AssistantFlyRow'
  },

  // ── The open page — ONE child keyed by its page (a new page rises in). It
  // lies on the layer's sheet: its cards take the solid card fill.
  CallOpen: {
    childrenDeps: ['meetFly'],
    minWidth: '0',
    children: (el, s) => {
      const p = el.call('assistantFlyCallDo', 'meetFlyPage', s.root)
      // Slides take their own stage (CallDeck below).
      if (!p || p.kind === 'deck') return []
      const m = el.call('assistantBoardMetrics', s)
      return [
        { ...p, cols: m.cols, gutter: m.gutter, cell: m.cell, square: m.square, onLayer: true }
      ]
    },
    childrenAs: 'state',
    childExtends: 'AssistantFlyPageBody'
  },

  // ── The open page when it is SLIDES (a Presentation ask in the call): the
  // deck's own slide on the layer, Previous / Next — the asker presents.
  CallDeck: {
    childrenDeps: ['meetFly'],
    minWidth: '0',
    width: '100%',
    marginInline: 'auto',
    boxSizing: 'border-box',
    style: (el, s) => el.call('assistantFlyPageStyle', s),
    children: (el, s) => {
      const vm = el.call('assistantFlyCallDeck', s.root)
      return vm ? [vm] : []
    },
    childrenAs: 'state',
    childExtends: 'AssistantFlyCallDeck'
  },

  // ── The call's later pages, folded ────────────────────────────────────────
  CallAfter: {
    childrenDeps: ['meetFly'],
    tag: 'ol',
    margin: '0',
    padding: '0',
    listStyle: 'none',
    flow: 'y',
    gap: 'X',
    minWidth: '0',
    width: '100%',
    marginInline: 'auto',
    boxSizing: 'border-box',
    style: (el, s) => el.call('assistantFlyPageStyle', s),
    children: (el, s) => {
      const pages = el.call('assistantFlyCallDo', 'meetFlyPages', s.root) || []
      const at = pages.findIndex((p) => p.open)
      return at < 0 ? [] : pages.filter((p, i) => i > at)
    },
    childrenAs: 'state',
    childExtends: 'AssistantFlyRow'
  },

  // ── The call so far, under its pages: the live summary — what was said,
  // what it caught (each applied with a press), what changed in the
  // workspace while it ran (meet components/LiveRecap.js) — as a card on the
  // layer's sheet. Made once the theater shows: the meet context is folded by
  // then, and the framework merges a part named before its component was
  // registered when its `if` turns true (`ifDeps`: the call's state, read
  // through the guarded door, so the condition re-runs).
  MeetLiveRecap: {
    if: (el, s) => !!el.call('assistantFlyCallDo', 'meetFlyTheaterShown', s.root),
    ifDeps: ['meetFly', 'meetView', 'currentPath'],
    width: '100%',
    maxHeight: 'none',
    overflowY: 'visible',
    marginInline: 'auto',
    boxSizing: 'border-box',
    padding: 'A',
    round: 'radiusCard',
    background: 'cardSolid',
    borderBottomWidth: '0',
    style: (el, s) => el.call('assistantFlyPageStyle', s)
  }
}
